﻿import React, { useState } from 'react';
import { connect } from 'react-redux';
import Valg from '../../Shared/Scripts/Models/Valg';
import * as ValgTypes from '../Store/Valg/Types';
import VeilederStegComponent, { VeilederStegDefaultProps, VeilederStegBaseProps } from '../../Shared/Scripts/Components/VeilederStegComponent';
import { setCollapsed } from '../../Shared/Scripts/Store/StegVisning/Actions';
import { EpiContext } from '../../Shared/Scripts/Store/EpiContext/Types';
import ForelopigUttalelseSkjemaSideDto from '../Models/ForelopigUttalelseSkjemaSideDto';
import { ForelopigUttalelseSkjemaSideAppState } from '../Store';
import contentResolver from '../../Shared/Scripts/Utilities/EpiContentResolver';
import { hun, han } from '../Models/Values';
import { DateTime } from 'luxon';
import LabelWithErrorComponent from '../../Shared/Scripts/Components/LabelWithErrorComponent';
import { LegendWithErrorComponent } from '../../Shared/Scripts/Components/LegendWithErrorComponent';

type Steg1ComponentOwnProps = VeilederStegDefaultProps & VeilederStegBaseProps & {
    ledetekst: JSX.Element | null,
    onChange: any,
    value?: Valg<ValgTypes.BrukerInfo>,
}

type Steg1DisplayProps = Steg1ComponentOwnProps & MapStateToProps;

const DATE_CONTAINER = 'js-veileder__dato-container';
const FULLNAME_CONTAINER = 'js-veileder__fultnavn-container';
const GENDER_CONTAINER = 'js-veileder__kjonn-container';

function Steg1ComponentVisning(props: Steg1DisplayProps): JSX.Element {
    const { epiContext } = props;
    
    const [inputNavn, setInputNavn] = useState('');
    const [inputKjonn, setInputKjonn] = useState('');
    const [inputDato, setInputDato] = useState('');
    const [inputMellom, setMellom] = useState<string | null>(null);
    const [fultnavnInputIsInErrorState, setfultnavnInputIsInErrorState] = useState(false);
    const [kjonnIsInErrorState, setkjonnIsInErrorState] = useState(false);
    const [datoIsInErrorState, setdatoIsInErrorState] = useState(false);


    function validateForm(): boolean {

        let navn: string | null | undefined = (document.getElementById("fultnavn-input") as HTMLInputElement)?.value;

        if(navn === null || navn === undefined || navn === '') {
            showElementError(FULLNAME_CONTAINER);
            setfultnavnInputIsInErrorState(true);
            return false;
        } else {
            hideElementError(FULLNAME_CONTAINER);
            setfultnavnInputIsInErrorState(false);
        }

        if(inputKjonn === null || inputKjonn === undefined || inputKjonn === '') {
            showElementError(GENDER_CONTAINER);
            setkjonnIsInErrorState(true);
            return false;
        } else {
            hideElementError(GENDER_CONTAINER);
            setkjonnIsInErrorState(false);
        }

        if(!isDateValid()) {
            showElementError(DATE_CONTAINER);
            setdatoIsInErrorState(true);
            return false;
        } else {
            hideElementError(DATE_CONTAINER);
            setdatoIsInErrorState(false);
        }

        return true;
    }

    function buttonClickHandler(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();

        const isValid = validateForm();
        if(!isValid){ return; }

        let valg: Valg<ValgTypes.BrukerInfo> = { id: '', displayValue: inputNavn, value: {fulltNavn: inputNavn, kjonn: inputKjonn, fodt: inputDato, mellom: inputMellom} }
        props.onChange(valg);
    }

    function navnInputChangeHandler(e: React.SyntheticEvent<HTMLInputElement>) {
        hideElementError(FULLNAME_CONTAINER);
        setInputNavn(e.currentTarget.value);
        setfultnavnInputIsInErrorState(false);
    }

    function kjonnInputClickHandler(e: React.SyntheticEvent<HTMLInputElement>) {
        hideElementError(GENDER_CONTAINER);
        setkjonnIsInErrorState(false);
        setInputKjonn(e.currentTarget.value);
    }

    function datoInputChangeHandler(e: React.SyntheticEvent<HTMLInputElement>) {
        hideElementError(DATE_CONTAINER);
        setdatoIsInErrorState(false);

        if(!isDateSupported()) {
            setDateInputManually(e.currentTarget.value);
        } else {
            setInputDato(e.currentTarget.value);
        }
    }

    function setDateInputManually(dateInput: string): void {
        let verdiKunMedTall: string = dateInput.replace(/[^0-9\.]/g, '');
        
        setInputDato(verdiKunMedTall);
    }

    function isDateValid(): boolean {

        let input: HTMLElement | null = document.getElementById("c-dato-input");

        if(input === null)
            return false;

        let dateInputValue: string = (input as HTMLInputElement).value;

        if(dateInputValue === '' || dateInputValue === null || dateInputValue === undefined) {
            setInputDato('');
            return false;
        }

        //datoen kan være splittet med både dot og dash, vi sjekker derfor begge.
        let dotSplitted: string[] = dateInputValue.split('.');
        let dashSplitted: string[] = dateInputValue.split('-');

        if(dotSplitted.length !== 3 && dashSplitted.length != 3) {
            return false;
        }

        let splitted: string[] = [];

        if(dotSplitted.length > 1) {
            splitted = dotSplitted;
        }

        if(dashSplitted.length > 1) {
            //snur rundt på dem på grunn av sjekkene nedenfor. 
            splitted[0] = dashSplitted[2];
            splitted[1] = dashSplitted[1];
            splitted[2] = dashSplitted[0];
        }

        if(splitted.length !== 3) {
            setInputDato(dateInputValue);
            return false;
        }

        if(splitted[0].length > 2 || parseInt(splitted[0]) > 31) {
            return false;
            //console.log("feil med dag i dato");
        }

        if(splitted[1].length > 2 || parseInt(splitted[1]) > 12) {
            return false;
            //console.log("feil med måned i dato");
        }

        if(splitted[2].length !== 4) {
            return false;
        }

        // Sjekker at dato ikke er i fremtiden eller eldre enn 100 år
        const inputDate = DateTime.fromISO(dateInputValue);
        const now = DateTime.now();
        const diff = now.diff(inputDate, "years").toObject().years;

        return now > inputDate && Math.floor(diff !== undefined ? diff : 0) <= 100;
    }

    function mellomChangeHandler (e: React.SyntheticEvent<HTMLInputElement>): void {
        if(e.currentTarget.files !== undefined && e.currentTarget.files !== null && e.currentTarget.files.length > 0) {
            setMellom(e.currentTarget.files[0].name);
        }
    }

    function isDateSupported(): boolean {
        let input = document.createElement('input');
        let value = 'a';
        input.setAttribute('type', 'date');
        input.setAttribute('value', value);
        return (input.value !== value);
    }

    function showElementError(elementId: string): void {
        let cont = document.getElementById(elementId);
        cont?.classList.add("c-text-input--feil");
        const inputElement = cont?.querySelector("input");
        inputElement?.focus();
    }

    function hideElementError(elementId: string): void {
        let cont = document.getElementById(elementId);
        cont?.classList.remove("c-text-input--feil");
    }

    function getDateByDiffFromToday(diff: number): string {
        const now: Date = new Date();
        let date: string = now.getDate() + "";
        let month: string = (now.getMonth() + 1) + "";
        const year: number = (now.getFullYear() - diff);

        if(date.length === 1)
            date = "0"+date;

        if(month.length === 1)
            month = "0"+month;

        return year + "-" + month + "-" + date;
    }
    
    return (
        <VeilederStegComponent
            tittel={props.tittel}
            forklaring={props.forklaring}
            canCollapse={false}
            displayElement={<div className="c-veileder__valgt-item">
                <span className="c-veileder__tekst c-veileder__tekst--valg">{props.value?.displayValue}</span>
            </div>}
            inputArea={
                <div onSubmit={() => buttonClickHandler} className="c-veileder__inputomrade c-veileder__skjulte-inputomrader">
                    <div className="c-veileder__inputomrade c-veileder__inputomrade-med-sosken">
                        <h4>{props.ledetekst}</h4>
                    </div>

                    <div className="c-veileder__inputomrade c-veileder__inputomrade-med-sosken c-veileder__inputomrade-med-skjult-sosken" id={FULLNAME_CONTAINER}>
                        <LabelWithErrorComponent
                            inputId="fultnavn-input"
                            showError={fultnavnInputIsInErrorState}
                            errorElement={contentResolver(epiContext, 'steg1UgyldigNavn')}>
                            {contentResolver(epiContext, 'steg1FulltNavn')}
                        </LabelWithErrorComponent>

                        <input maxLength={250} required id="fultnavn-input" autoComplete="name"  onChange={navnInputChangeHandler} className="c-text-input__input" type="text" value={inputNavn} />
                    </div>
                    {/* Honeypot for bots */}
                    <div className="c-veileder__inputomrade c-veileder__inputomrade-med-sosken c-veileder__inputomrade--forfoereren">
                        <label htmlFor="mellom" className="c-text-input__label">Bilde</label>
                        <input className="c-text-input__input" autoComplete="off" aria-hidden="true" tabIndex={-1} type="file" onChange={mellomChangeHandler} />
                    </div>
                    <div className="c-veileder__inputomrade c-veileder__inputomrade-med-sosken" id={GENDER_CONTAINER}>
                        <fieldset className="c-veileder__fieldset">
                            <LegendWithErrorComponent
                                feilMeldingsId="velg-kjonn-legend"
                                showError={kjonnIsInErrorState}
                                errorElement={contentResolver(epiContext, 'steg1IkkeValgtKjonn')}
                            >
                                {contentResolver(epiContext, 'steg1Kjonn')}
                            </LegendWithErrorComponent>

                            <div className="c-radio-group">
                                <div className="c-radio-input">
                                    <input
                                        onClick={kjonnInputClickHandler}
                                        value={hun}
                                        className="c-radio-input__input"
                                        name="radio-kjonn"
                                        type="radio"
                                        id="radio-kjonn-kvinne"
                                        aria-describedby="velg-kjonn-legend"
                                    />
                                    <label htmlFor="radio-kjonn-kvinne">
                                        {contentResolver(epiContext, 'steg1HunKjonn')}
                                    </label>
                                </div>
                                <div className="c-radio-input">
                                    <input
                                        onClick={kjonnInputClickHandler}
                                        value={han}
                                        className="c-radio-input__input"
                                        name="radio-kjonn"
                                        type="radio"
                                        id="radio-kjonn-mann"
                                        aria-describedby="velg-kjonn-legend"
                                    />
                                    <label htmlFor="radio-kjonn-mann">
                                        {contentResolver(epiContext, 'steg1HanKjonn')}
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                    <div className="c-veileder__inputomrade c-veileder__inputomrade-med-sosken" id={DATE_CONTAINER}>       
                        <div>
                            <LabelWithErrorComponent
                                inputId="c-dato-input"
                                showError={datoIsInErrorState}
                                errorElement={contentResolver(epiContext, 'steg1FeilDato')}>
                                {contentResolver(epiContext, 'steg1Fodt')}
                            </LabelWithErrorComponent>
                            <input required id="c-dato-input" autoComplete="bday" min={getDateByDiffFromToday(100)} max={getDateByDiffFromToday(0)} onChange={datoInputChangeHandler} className="c-text-input__input" type="date" value={inputDato} />
                        </div>
                    </div>
                    <div className="c-veileder__content--cta-area">
                        <button onClick={buttonClickHandler} className="c-knapp c-knapp--cta c-knapp--secondary">{contentResolver(epiContext, 'steg1Knappetekst')}</button>
                    </div>
                </div>
            }
            sporsmalId={props.sporsmalId}
            classModifier="c-veileder__panel--input-white"
        />
    )
}

interface MapStateToProps {
    epiContext: EpiContext<ForelopigUttalelseSkjemaSideDto>;
}

export default connect(
    (state: ForelopigUttalelseSkjemaSideAppState, ownProps: Steg1ComponentOwnProps): MapStateToProps => {
        return {
            epiContext: state.epiContext,
        };
    },
    {
        stegVisningSetCollapsed: setCollapsed,
    },
)(Steg1ComponentVisning);